import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { GET_BLUEPRINTS, GET_BLUEPRINT_VERSIONS } from '../graphql/queries';
import { TRIGGER_BUILD_MATRIX, STOP_WORKFLOW } from '../graphql/mutations';
import OsIcon from '../components/OsIcon';
import { formatArch } from '../utils/os-icons';

export default function BuildMatrix() {
  const { data: bpData, loading: bpLoading, error: bpError } = useQuery(GET_BLUEPRINTS);
  const { data: versionData } = useQuery(GET_BLUEPRINT_VERSIONS);
  const [triggerMatrix, { loading: triggering }] = useMutation(TRIGGER_BUILD_MATRIX);
  const [stopWorkflow, { loading: stopping }] = useMutation(STOP_WORKFLOW);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({
    awsRegion: 'us-east-1',
    instanceType: 't3.medium',
    version: 'latest',
  });
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState({});

  const blueprints = bpData?.listBlueprints?.items || [];
  const versions = versionData?.listBlueprintVersions || [];

  const toggleBlueprint = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelected(next);
  };

  const selectByFilter = (filterFn) => {
    const next = new Set(selected);
    blueprints.filter(filterFn).forEach((bp) => next.add(bp.name));
    setSelected(next);
  };

  const handleSelectAllLinux = () => {
    selectByFilter((bp) => bp.osType?.toLowerCase() === 'linux');
  };

  const handleSelectAllWindows = () => {
    selectByFilter((bp) => bp.osType?.toLowerCase() === 'windows');
  };

  const handleSelectAllArm64 = () => {
    selectByFilter((bp) => {
      const arch = bp.architecture?.toLowerCase() || '';
      return arch.startsWith('arm64') || arch === 'arm' || arch === 'aarch64';
    });
  };

  const handleSelectAllX86 = () => {
    selectByFilter((bp) => {
      const arch = bp.architecture?.toLowerCase() || 'x86_64';
      return arch.startsWith('x86_64') || arch === 'x86' || arch === 'amd64' || arch === 'i386';
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(blueprints.map((bp) => bp.name)));
  };

  const handleClearAll = () => {
    setSelected(new Set());
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.size === 0) return;
    const blueprintNames = Array.from(selected);
    const initialProgress = {};
    blueprintNames.forEach((name) => { initialProgress[name] = 'pending'; });
    setProgress(initialProgress);

    try {
      const { data } = await triggerMatrix({
        variables: {
          input: {
            blueprints: blueprintNames,
            awsRegion: form.awsRegion,
            instanceType: form.instanceType,
            version: form.version,
          },
        },
      });
      setResult(data.triggerBuildMatrix);
      const runningProgress = {};
      blueprintNames.forEach((name) => { runningProgress[name] = 'running'; });
      setProgress(runningProgress);
    } catch (err) {
      toast.error(`Build matrix failed: ${err.message}`);
      const failedProgress = {};
      blueprintNames.forEach((name) => { failedProgress[name] = 'failed'; });
      setProgress(failedProgress);
    }
  };

  if (bpLoading) return <div className="loading">Loading blueprints...</div>;
  if (bpError) return <div className="error">Failed to load blueprints: {bpError.message}</div>;

  return (
    <div>
      <h2 className="page-title">Build Matrix</h2>

      <div className="card">
        <h3>Select Blueprints</h3>
        <div className="actions" style={{ marginBottom: 16, marginTop: 0 }}>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={handleSelectAll}>Select All</button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#dfe6e9' }} onClick={handleClearAll}>Clear All</button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#00b894', color: '#fff' }} onClick={handleSelectAllLinux}>Select All Linux</button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#0984e3', color: '#fff' }} onClick={handleSelectAllWindows}>Select All Windows</button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#e17055', color: '#fff' }} onClick={handleSelectAllX86}>Select All x86_64</button>
          <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#6c5ce7', color: '#fff' }} onClick={handleSelectAllArm64}>Select All ARM64</button>
        </div>
        {blueprints.length === 0 ? (
          <p>No blueprints available. <Link to="/blueprints" className="link">View blueprints</Link> to get started.</p>
        ) : (
          <div className="matrix-grid">
            {blueprints.map((bp) => (
              <label key={bp.name} className={`matrix-item ${selected.has(bp.name) ? 'matrix-item-selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected.has(bp.name)}
                  onChange={() => toggleBlueprint(bp.name)}
                />
                <div className="matrix-item-info">
                  <OsIcon name={bp.name} size={22} />
                  <strong>{bp.name}</strong>
                  <span style={{ fontSize: 12, color: '#636e72' }}>
                    {formatArch(bp.architecture)}
                  </span>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h3>Build Configuration</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>AWS Region</label>
              <select value={form.awsRegion} onChange={set('awsRegion')}>
                <option value="us-east-1">us-east-1</option>
                <option value="us-west-2">us-west-2</option>
                <option value="eu-west-1">eu-west-1</option>
                <option value="ap-southeast-2">ap-southeast-2</option>
              </select>
            </div>
            <div className="form-group">
              <label>Instance Type</label>
              <select value={form.instanceType} onChange={set('instanceType')}>
                <option value="t3.medium">t3.medium</option>
                <option value="t3.large">t3.large</option>
                <option value="m5.large">m5.large</option>
                <option value="c5.xlarge">c5.xlarge</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Blueprint Version</label>
              <select value={form.version} onChange={set('version')}>
                <option value="latest">latest (synced from main)</option>
                {versions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Selected</label>
              <p style={{ padding: '8px 0', fontSize: 14 }}>{selected.size} blueprint(s) selected</p>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={triggering || selected.size === 0}>
            {triggering ? 'Submitting Matrix Build...' : `Start Build Matrix (${selected.size})`}
          </button>
        </form>
      </div>

      {result && (
        <div className="card">
          <h3>Build Matrix Status</h3>
          <div className="detail-grid" style={{ marginBottom: 16 }}>
            <div className="detail-item"><label>Workflow</label><p>{result.workflowName || '-'}</p></div>
            <div className="detail-item"><label>Status</label>
              <p><span className={`badge badge-${result.status === 'submitted' ? 'success' : result.status === 'failed' ? 'error' : 'info'}`}>{result.status}</span></p>
            </div>
            {result.message && <div className="detail-item" style={{ gridColumn: '1 / -1' }}><label>Message</label><p>{result.message}</p></div>}
          </div>
          {result.status === 'submitted' && (
            <div className="actions" style={{ marginTop: 0 }}>
              <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }}
                disabled={stopping}
                onClick={async () => {
                  for (const name of result.blueprints || []) {
                    const wfName = `packer-build-${name}-`;
                    try { await stopWorkflow({ variables: { workflowName: wfName } }); } catch {}
                  }
                }}>
                {stopping ? 'Cancelling...' : 'Cancel All Builds'}
              </button>
            </div>
          )}
        </div>
      )}

      {Object.keys(progress).length > 0 && (
        <div className="card">
          <h3>Blueprint Progress</h3>
          <div className="matrix-grid">
            {Object.entries(progress).map(([name, status]) => (
              <Link key={name} to={`/build/packer-build-${name}-`} className="matrix-item" style={{ textDecoration: 'none', color: 'inherit' }}>
                <span className={`badge badge-${status === 'running' ? 'info' : status === 'failed' ? 'error' : status === 'completed' ? 'success' : 'pending'}`}>
                  {status}
                </span>
                <div className="matrix-item-info">
                  <OsIcon name={name} size={18} />
                  <strong>{name}</strong>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
